import { ThemeEvents } from '@theme/events';

/**
 * Display string for a variant (matches Liquid `variant.title` / Shopify storefront API).
 * @param {Record<string, unknown> | null | undefined} variant
 */
function variantDisplayTitle(variant) {
  if (!variant) return '';
  var t = variant.title;
  if (t != null && String(t).trim() !== '') return String(t).trim();
  var opts = variant.options;
  if (Array.isArray(opts) && opts.length) {
    return opts
      .map(function (o) {
        return o == null ? '' : String(o).trim();
      })
      .filter(Boolean)
      .join(' / ');
  }
  var parts = [variant.option1, variant.option2, variant.option3].filter(function (x) {
    return x != null && String(x).trim() !== '';
  });
  return parts.map(String).join(' / ');
}

/**
 * `data-input-id` is `{{ product_option.position }}-{{ index }}` (Shopify option order, not theme UI order).
 * @param {string | null} dataInputId
 */
function parseOptionPositionFromDataInputId(dataInputId) {
  if (!dataInputId) return 9999;
  var m = String(dataInputId).match(/^(\d+)/);
  if (!m) return 9999;
  var n = parseInt(m[1], 10);
  return isNaN(n) ? 9999 : n;
}

/**
 * Build the same segment order as `variant.title`: option1, option2, … even when the picker UI reorders rows.
 * @param {Element | null} picker
 */
function collectLabelsInShopifyOptionOrder(picker) {
  if (!picker) return '';
  var form = picker.querySelector('form');
  if (!form) return '';
  /** @type {{ pos: number, v: string }[]} */
  var rows = [];

  form.querySelectorAll('input.js-variant-option-input:checked').forEach(function (inp) {
    var pos = parseOptionPositionFromDataInputId(inp.getAttribute('data-input-id'));
    var v = (inp.getAttribute('data-js-option-value') || inp.value || '').trim();
    if (v) rows.push({ pos: pos, v: v });
  });

  form.querySelectorAll('select.variant-option__select, select.js-variant-option-input').forEach(function (sel) {
    var opt = sel.selectedOptions && sel.selectedOptions[0];
    if (!opt) return;
    var pos = parseOptionPositionFromDataInputId(opt.getAttribute('data-input-id'));
    var v = (opt.getAttribute('data-js-option-value') || opt.value || opt.textContent || '').trim();
    if (v) rows.push({ pos: pos, v: v });
  });

  rows.sort(function (a, b) {
    return a.pos - b.pos;
  });

  return rows
    .map(function (r) {
      return r.v;
    })
    .join(' / ');
}

/**
 * @param {string} text
 * @param {string | undefined} productId
 */
function setVariantSelectedInformationText(text, productId) {
  if (!text) return;
  document.querySelectorAll('.js-variant-selected-information').forEach(function (el) {
    var root = el.closest('dynamic-plan-info');
    if (root && root.dataset.productId && productId && String(root.dataset.productId) !== String(productId)) {
      return;
    }
    el.textContent = text;
  });
}

/** After section morph: authoritative variant from `VariantUpdateEvent` (same as product-form). */
function onVariantUpdate(ev) {
  var variant = ev.detail && ev.detail.resource;
  var productId = ev.detail && ev.detail.data && ev.detail.data.productId;
  if (!variant) return;
  setVariantSelectedInformationText(variantDisplayTitle(variant), productId);
}

/** While fetch runs: mirror `variant.title` order using Shopify option position from `data-input-id`. */
function onVariantSelected(ev) {
  var target = ev.target;
  if (!(target instanceof Element)) return;
  var picker = target.closest('variant-picker');
  if (!picker) return;
  var productId = picker.dataset.productId;
  var text = collectLabelsInShopifyOptionOrder(picker);
  if (text) setVariantSelectedInformationText(text, productId);
}

/** Align with default / URL variant on load (picker DOM is source of truth for order). */
function syncVariantSelectedInformationFromPickerOnce() {
  var picker = document.querySelector('variant-picker[data-template-product-match="true"]');
  if (!picker) return;
  var text = collectLabelsInShopifyOptionOrder(picker);
  if (!text) return;
  setVariantSelectedInformationText(text, picker.dataset.productId);
}

document.addEventListener(ThemeEvents.variantUpdate, onVariantUpdate);
document.addEventListener(ThemeEvents.variantSelected, onVariantSelected);

function scheduleInitialSync() {
  requestAnimationFrame(function () {
    requestAnimationFrame(syncVariantSelectedInformationFromPickerOnce);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleInitialSync);
} else {
  scheduleInitialSync();
}
